import { FC, useCallback } from "react";
import { Link } from "@tanstack/react-router";

import { LabelAndValue } from "@/blocks/LabelAndValue";
import { RmButton } from "@/components/RmButton/RmButton";
import { RmCardTitle } from "@/components/RmCard";
import { RmLabel } from "@/components/RmLabel";
import { RmSeparator } from "@/components/RmSeparator";
import {
  RmSheetContent,
  RmSheetHeader,
  RmSheetTitle,
} from "@/components/RmSheet";
import { BaseSwitchInput } from "@/components/RmSwitchInput/BaseSwitchInput";
import { StudentType } from "@/domain/student";
import { useCurrentStaff } from "@/hooks/useCurrentStaff";
import { useCurrentStudent } from "@/hooks/useCurrentStudent";
import { useIsDarkMode } from "@/hooks/useIsDarkMode";
import { useTheme } from "@/hooks/useTheme";
import { formatPhoneNumber } from "@/utils/helpers";

export const AccountDetails: FC = () => {
  const { setTheme } = useTheme();

  const isDarkMode = useIsDarkMode();

  const currentStaff = useCurrentStaff();
  const currentStudent = useCurrentStudent();

  const currentUser = currentStaff ?? currentStudent;

  const firstName = currentUser?.first_name ?? "";
  const lastName = currentUser?.last_name ?? "";
  const email = currentUser?.email ?? "";
  const phone = formatPhoneNumber(
    (currentUser as StudentType)?.phone_number ?? "",
  )!;

  const toggleTheme = useCallback(() => {
    setTheme(isDarkMode ? "light" : "dark");
  }, [isDarkMode, setTheme]);

  return (
    <RmSheetContent>
      <RmSheetHeader>
        <RmSheetTitle i18nKey="main:account.title" />
      </RmSheetHeader>
      <div className="mt-2 flex w-full flex-col gap-6">
        <RmCardTitle i18nKey="main:account.profileInformation" />
        <div className="flex flex-col gap-6">
          <div className="grid grid-cols-1 gap-y-6 md:grid-cols-2">
            <LabelAndValue
              wrapperClassName="col-span-1"
              labelI18nKey="common:firstName"
              value={firstName}
            />
            <LabelAndValue
              wrapperClassName="col-span-1"
              labelI18nKey="common:lastName"
              value={lastName}
            />
          </div>
          <div className="grid grid-cols-1 gap-y-6 md:grid-cols-2">
            <LabelAndValue
              wrapperClassName="col-span-1"
              labelI18nKey="common:email"
              value={email}
            />
            <LabelAndValue
              wrapperClassName="col-span-1"
              labelI18nKey="main:students.details.labels.phone"
              value={phone}
              isHidden={!phone}
            />
          </div>
        </div>

        <RmCardTitle i18nKey="main:account.settings" />
        <div>
          <div className="flex flex-row items-center gap-2">
            <BaseSwitchInput checked={isDarkMode} onClick={toggleTheme} />
            <RmLabel i18nKey="main:account.darkMode" />
          </div>
        </div>
      </div>
      <RmSeparator className="my-6" />
      <div className="self-center">
        <Link to="/logout">
          <RmButton variant="ghost" i18nKey="common:logout" />
        </Link>
      </div>
    </RmSheetContent>
  );
};
